import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { classNames } from '@/utils';
import { Cell, Pie, PieChart as RechartsPieChart } from 'recharts';
import { DEFAULT_ANIMATION } from '../constants';
import { PieChartProps } from '../types';
import { formatPercentage, formatValue, getChartColors } from '../utils';
import { ChartEmpty, ChartError, ChartLoading } from './ChartStates';

export function PieChart({
  data,
  format = 'number',
  currencyCode,
  colors,
  innerRadius = 80,
  outerRadius = 100,
  tooltip,
  legend,
  showLegend = true,
  animate = DEFAULT_ANIMATION.enabled,
  loading,
  error,
  emptyState,
  fillParent = false,
  minLegendItems = [],
  className = '',
}: PieChartProps) {
  // Handle loading state
  if (loading) {
    return <ChartLoading fillParent className={className} />;
  }

  // Handle error state
  if (error) {
    return <ChartError error={error} fillParent className={className} />;
  }

  // Ensure minimum legend items are present
  const normalizedData = [...data];
  if (minLegendItems && minLegendItems.length > 0) {
    const existingLabels = new Set(
      data.map((item) => item.label?.toLowerCase()),
    );
    minLegendItems.forEach((requiredLabel) => {
      if (!existingLabels.has(requiredLabel.toLowerCase())) {
        normalizedData.push({ label: requiredLabel, value: 0 });
      }
    });
  }

  // Handle empty state (check after adding minLegendItems)
  const hasData = normalizedData.some((item) => item.value > 0);
  if (!hasData) {
    return (
      <ChartEmpty fillParent className={className}>
        {emptyState}
      </ChartEmpty>
    );
  }

  // Get colors for slices
  const sliceColors = colors || getChartColors(normalizedData.length);

  // Build chart config
  const chartConfig: ChartConfig = normalizedData.reduce(
    (config, item, index) => {
      config[item.label] = {
        label: item.label.charAt(0).toUpperCase() + item.label.slice(1),
        color: sliceColors[index],
      };
      return config;
    },
    {} as ChartConfig,
  );

  // Calculate total (only non-zero values for display)
  const total = normalizedData.reduce((sum, item) => sum + item.value, 0);

  // Custom formatter for tooltip
  const tooltipFormatter =
    tooltip?.formatter ||
    ((value: number, _name: string, props: any) => {
      const percentage = total > 0 ? (props.payload.value / total) * 100 : 0;
      const label =
        props.payload.label.charAt(0).toUpperCase() +
        props.payload.label.slice(1);
      return (
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-muted-foreground">{label}</span>
          <span className="text-foreground font-mono font-medium tabular-nums">
            {formatValue(value, format, currencyCode)} (
            {formatPercentage(percentage)})
          </span>
        </div>
      );
    });

  const containerClass = fillParent
    ? `absolute inset-0 ${className}`
    : `w-full min-h-64 ${className}`;

  // Determine legend position (default to bottom)
  const legendPosition = legend?.position || 'bottom';
  const isHorizontalLayout = legendPosition === 'bottom';

  return (
    <div className={containerClass}>
      <div
        className={classNames(
          'flex h-full',
          legendPosition === 'bottom'
            ? 'flex-col items-center gap-4'
            : 'flex-row justify-between gap-4 xl:gap-8',
        )}
      >
        {/* Chart */}
        <div
          className={classNames(
            'relative aspect-square',
            (!showLegend || legendPosition === 'bottom') && 'flex-1',
          )}
        >
          <ChartContainer
            config={chartConfig}
            className="absolute inset-0 h-full w-full"
          >
            <RechartsPieChart>
              {tooltip?.enabled !== false && (
                <ChartTooltip
                  cursor={false}
                  content={
                    typeof tooltip?.content === 'function' ? (
                      tooltip.content
                    ) : tooltip?.content ? (
                      () => tooltip.content as any
                    ) : (
                      <ChartTooltipContent
                        formatter={tooltipFormatter as any}
                      />
                    )
                  }
                />
              )}
              <Pie
                data={normalizedData}
                dataKey="value"
                nameKey="label"
                // cx="50%"
                // cy="50%"
                innerRadius={`${innerRadius}%`}
                outerRadius={`${outerRadius}%`}
                isAnimationActive={animate}
                animationDuration={animate ? DEFAULT_ANIMATION.duration : 0}
              >
                {normalizedData.map((entry, index) => (
                  <Cell key={`cell-${entry.label}`} fill={sliceColors[index]} />
                ))}
              </Pie>
            </RechartsPieChart>
          </ChartContainer>
        </div>

        {/* Custom Legend */}
        {showLegend && (
          <div
            className={classNames(
              'flex shrink-0 gap-2',
              isHorizontalLayout
                ? 'flex-row flex-wrap justify-center'
                : 'flex-col justify-center',
            )}
          >
            {normalizedData.map((item, index) => {
              const percentage = total > 0 ? (item.value / total) * 100 : 0;
              return (
                <div
                  key={item.label}
                  className={classNames(
                    'flex items-center text-sm',
                    isHorizontalLayout ? 'gap-2' : 'justify-between gap-4',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: sliceColors[index] }}
                    />
                    <span className="text-gray-700">
                      {item.label.charAt(0).toUpperCase() + item.label.slice(1)}
                    </span>
                  </div>
                  {!isHorizontalLayout && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {formatValue(item.value, format, currencyCode)}
                      </span>
                      <span className="text-gray-500">
                        ({formatPercentage(percentage)})
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
